import { FormEvent, useState } from "react";
import { useCanViewAllocationReport } from "@/services/AuthService";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLazyApi } from "@/hooks/useApi";
import { allocationsService } from "@/services/AllocationsService";
import { AllocationAgentSummary, AllocationReportRow, AllocationOutcomeResponse } from "@/types/allocations";
import { Navigate } from "react-router-dom";
import { useAppData } from "@/context/AppDataContext";

function AllocationReportLoginWrapper() {
  const { isLoading } = useAppData();
  const canView = useCanViewAllocationReport();

  if (isLoading)
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-col justify-start gap-3 px-4 pt-3 md:flex-row md:items-center">
          <h1 className="text-2xl font-semibold">Allocation Report</h1>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );

  if (!canView) return <Navigate to="/pipeline" replace />;

  return <AllocationReport />;
}

const formatPercent = (value: number | null): string => (value === null ? "-" : `${value.toFixed(1)}%`);

const formatDecisionReason = (row: AllocationReportRow): string[] => {
  const reasons: string[] = [];
  const normalizedReason = row.reason?.trim() ?? "";

  if (normalizedReason) {
    if (normalizedReason.toLowerCase() === "not top rank" && row.rankPosition !== null) {
      reasons.push(`Not Top Rank (Rank was ${row.rankPosition})`);
    } else {
      reasons.push(normalizedReason);
    }
  }

  if (row.detail) {
    reasons.push(row.detail);
  }

  return reasons;
};

const sortAllocationLogs = (rows: AllocationReportRow[]): AllocationReportRow[] => {
  const sortedRows = [...rows].sort(
    (a, b) => (a.rankPosition ?? Number.MAX_SAFE_INTEGER) - (b.rankPosition ?? Number.MAX_SAFE_INTEGER),
  );
  return sortedRows;
};

export function AllocationReport() {
  const [applicationIdInput, setApplicationIdInput] = useState("");
  const [searchedApplicationId, setSearchedApplicationId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<AllocationOutcomeResponse | null>(null);
  const { execute, loading, error } = useLazyApi<AllocationOutcomeResponse | null>();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const applicationId = applicationIdInput.trim();
    if (!applicationId) {
      setSearchedApplicationId(null);
      setResult(null);
      setValidationError("Enter an Application ID to view allocation details.");
      return;
    }

    setValidationError(null);
    setSearchedApplicationId(applicationId);
    setResult(null);
    const lookupResult = await execute((signal) => allocationsService.getAllocationReport(applicationId, signal));
    setResult(lookupResult);
  };

  const hasError = validationError ?? error;
  const showEmptyState = searchedApplicationId && !loading && !result && !hasError;
  const matchedAgents = result?.matchedAgents ?? [];
  const rankedLog = result ? sortAllocationLogs(result.allocationLog) : [];

  return (
    <div className="flex h-full flex-col gap-4 px-4 py-3">
      <div>
        <h1 className="text-2xl font-semibold">Allocation Lookup</h1>
        <p className="text-muted-foreground text-sm">Search by Application ID to retrieve allocation details.</p>
      </div>

      <form className="flex max-w-xl items-end gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-sm font-medium" htmlFor="application-id-input">
            Application ID
          </label>
          <Input
            id="application-id-input"
            value={applicationIdInput}
            onChange={(event) => setApplicationIdInput(event.target.value)}
            placeholder="e.g. 123456"
          />
        </div>
        <Button type="submit" disabled={loading}>
          {loading ? "Retrieving..." : "Retrieve"}
        </Button>
      </form>

      {hasError && (
        <div className="border-destructive text-destructive rounded-md border p-3 text-sm" role="alert">
          {hasError}
        </div>
      )}

      {showEmptyState && (
        <div className="rounded-md border p-3 text-sm">
          No allocation data found for Application ID <strong>{searchedApplicationId}</strong>.
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-4">
          <section className="rounded-md border p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="mb-1 text-base font-semibold">Allocated Agent</h2>
                {result.topRankedAgent ? (
                  <p className="text-sm">
                    {result.topRankedAgent.agentName} ({result.topRankedAgent.agentDepartmentId})
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm">No top ranked agent.</p>
                )}
              </div>
              <div>
                <h2 className="mb-1 text-base font-semibold">Required Skills</h2>
                {result.requiredSkills.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {result.requiredSkills.map((skill) => (
                      <Badge key={skill} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No required skills identified.</p>
                )}
              </div>
              <div>
                <h2 className="mb-1 text-base font-semibold">Shift Date</h2>
                <p className="text-sm">{new Date(result.shiftDate).toLocaleDateString()}</p>
              </div>
            </div>
          </section>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-base font-semibold">Matched Agents ({matchedAgents.length})</h2>
            {matchedAgents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2">AgentID</th>
                      <th className="p-2">Agent</th>
                      <th className="p-2">Department</th>
                      <th className="p-2">Allocated</th>
                      <th className="p-2">Paid Out</th>
                      <th className="p-2">Conversion</th>
                      <th className="p-2">Lead Source Allocated</th>
                      <th className="p-2">Lead Source Paid Out</th>
                      <th className="p-2">Lead Source Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchedAgents.map((agent: AllocationAgentSummary) => (
                      <tr
                        key={agent.agentId}
                        className={`border-b last:border-0 ${
                          agent.agentId === result.topRankedAgent?.agentId ? "bg-primary/5" : ""
                        }`}
                      >
                        <td className="p-2">{agent.agentId}</td>
                        <td className="p-2 font-medium">{agent.agentName}</td>
                        <td className="p-2">{agent.agentDepartmentId}</td>
                        <td className="p-2">{agent.allocatedCount}</td>
                        <td className="p-2">{agent.paidOutCount}</td>
                        <td className="p-2">{formatPercent(agent.conversion)}</td>
                        <td className="p-2">{agent.leadSource.allocatedCount}</td>
                        <td className="p-2">{agent.leadSource.paidOutCount}</td>
                        <td className="p-2">{formatPercent(agent.leadSource.conversion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No matched agents for this application.</p>
            )}
          </section>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-base font-semibold">Allocation Decisions ({rankedLog.length})</h2>
            {rankedLog.length > 0 ? (
              <div className="space-y-2">
                {rankedLog.map((row, index) => (
                  <div key={`${row.agentId}-${index}`} className="rounded-md border p-2 text-sm">
                    <p className="font-medium">
                      Agent {row.agentId} - {row.allocated ? "Allocated" : "Not Allocated"}
                      {row.rankPosition !== null ? ` (Rank ${row.rankPosition})` : ""}
                    </p>
                    <p className="text-muted-foreground">{formatDecisionReason(row).join(" | ") || "-"}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No ranked or allocated agents were logged.</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export const AllocationReportPage = AllocationReportLoginWrapper;
